import React, { useEffect, useState } from 'react'
import { useSelector } from 'hooks/redux'
import PropTypes from 'prop-types'
import {
  getFeaturePreviousStatus,
  getCurrentFeatureId,
} from 'selectors/feature/featureMetadata'
import { getCanEditFeature } from 'selectors/authorization/checPlus'
import strings from 'utils/strings'
import { NEW, RETURNED, ACTIVE, OBSOLETE, TEST } from 'utils/constants'
import { isFunction } from 'utils/types'
import { useFeatureRevisions } from 'components/feature/FeatureRevision/useFeatureRevision'

import {
  ActiveButton,
  ObsoleteButton,
  TestButton,
  DesignButton,
} from 'components/feature/LifeCycleButtons'
import LifeCycleEditor from 'components/feature/LifeCycleEditor'
import ParameterValue from 'components/ParameterValue'
import ContextualHelp from 'components/ContextualHelp'
import ContextualHelpVehicleSearch from 'components/ContextualHelp/ContextualHelpVehicleSearch'
import ObsoleteFeature from 'components/feature/ObsoleteFeature/ObsoleteFeature'
import { hasRights } from 'selectors/authorization'
import { OBSOLETE_FEATURE } from 'constants/authorization'
import { getIsSimulating } from 'selectors/navigator'
import FeatureOverflowMenu from '../FeatureOverflowMenu'
import {
  SingleStatusButton,
  StyledFeatureStatus,
  StatusContainer,
  ParameterStatusContainer,
} from './FeatureStatus.style'

const FeatureStatus = ({
  setSendToTestClicked,
  setStatus,
  moduleName,
  revision,
  featureIsValid,
  status,
  isEditing,
  replaceRoute,
  isHistoryRoute,
  setConfirmActivate,
  setConfirmObsolete,
  renderHarnessBtn,
}) => {
  const [showHelp, setShowHelp] = useState(false)
  const canEditFeature = useSelector(getCanEditFeature)
  const previousStatus = useSelector(getFeaturePreviousStatus)
  const isObsoleteFeatureEnabled = useSelector(hasRights, {
    rights: [OBSOLETE_FEATURE],
  })
  const isSimulating = useSelector(getIsSimulating)

  // Get current feature ID and revision data
  const currentFeatureId = useSelector(getCurrentFeatureId)
  const featureRevisions = useFeatureRevisions(currentFeatureId)
  const isFeatureLatestRevision = useSelector((state) =>
    state.getIn(
      [
        'feature',
        'currentFeature',
        'feature',
        currentFeatureId,
        'isFeatureLatestRevision',
      ],
      true // Default to true (allow obsolete) if not set
    )
  )

  // Check if there are newer revisions that are not yet Active
  // Returns true if the Feature should NOT be allowed to obsolete due to non-active revisions
  const hasNonActiveNewerRevisions = () => {
    // If this is the latest revision, no newer revisions exist - always allow obsolete
    if (isFeatureLatestRevision === true) {
      return false
    }

    // If not the latest revision but no revisions array data, be conservative and block
    // (this means the backend says there are newer revisions but we don't have the details)
    if (!featureRevisions || featureRevisions.length === 0) {
      return true
    }

    // Revisions array is ordered oldest to newest (confirmed from useFeatureRevision.jsx:88)
    // Find current feature's position in the revisions array
    const currentIndex = featureRevisions.findIndex(
      (rev) =>
        rev.partNumber === currentFeatureId ||
        rev.uri?.includes(currentFeatureId)
    )

    // If current feature not found in array but isFeatureLatestRevision is false, be conservative
    if (currentIndex === -1) {
      return true
    }

    // Get all revisions after current
    const newerRevisions = featureRevisions.slice(currentIndex + 1)

    // If no newer revisions found but flag says not latest, be conservative and block
    if (newerRevisions.length === 0) {
      return true
    }

    // Return true (block) if ANY newer revision is not Active
    // Return false (allow) if ALL newer revisions are Active
    return newerRevisions.some((rev) => rev.status !== ACTIVE)
  }

  useEffect(() => {
    if (previousStatus === RETURNED && status === TEST) {
      setShowHelp(true)
    } else {
      setShowHelp(false)
    }
  }, [previousStatus, status])

  const isActive = status === ACTIVE
  const isObsolete = status === OBSOLETE
  const inTest = status === TEST
  const inDesign = status === NEW || status === RETURNED

  const statusTranslation = strings[status.toLowerCase()]

  const CurrentStatusButton = {
    [ACTIVE]: ActiveButton,
    [OBSOLETE]: ObsoleteButton,
    [NEW]: DesignButton,
    [RETURNED]: DesignButton,
    [TEST]: TestButton,
  }[status]

  const paramValue = inDesign ? `${strings.design} ` : statusTranslation
  const paramPostscript = inDesign ? `(${statusTranslation.toUpperCase()})` : ''

  const canObsoleteAFeature =
    isActive &&
    !isObsolete &&
    isObsoleteFeatureEnabled &&
    !hasNonActiveNewerRevisions() // Don't allow obsolete if newer revisions exist that aren't Active

  return (
    <StatusContainer>
      <StyledFeatureStatus>
        <ParameterStatusContainer isEditing={isEditing}>
          <ParameterValue
            label={strings.feature_status}
            value={paramValue}
            postscript={paramPostscript}
            size="small"
          />
          {isFunction(renderHarnessBtn) && renderHarnessBtn()}
        </ParameterStatusContainer>
        {canObsoleteAFeature && (
          <ObsoleteFeature
            currentStatus={status}
            setConfirmObsolete={setConfirmObsolete}
          />
        )}
        {!canObsoleteAFeature &&
        (isActive || isObsolete || isHistoryRoute || !canEditFeature) ? (
          <SingleStatusButton
            xmlns="http://www.w3.org/2000/svg"
            width="35"
            height="35"
            viewBox="0 0 40 40"
            marginLeft={inTest && '-65px'}
          >
            <g fill="none" fillRule="evenodd">
              {
                <CurrentStatusButton
                  isSelected={true}
                  withLabel={false}
                  transformX={8}
                  transformY={18}
                />
              }
            </g>
          </SingleStatusButton>
        ) : (
          <>
            {canEditFeature && !canObsoleteAFeature && (
              <LifeCycleEditor
                currentStatus={status}
                moduleName={moduleName}
                revision={revision}
                setStatus={setStatus}
                featureIsValid={featureIsValid}
                setFailedSendToTest={setSendToTestClicked}
                setConfirmActivate={setConfirmActivate}
              />
            )}
            {showHelp && (
              <ContextualHelp
                contextMsg={strings.contextual_help_change_status_of_feature}
                contentMsg={strings.contextual_help_changes_to_this_feature}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    fill="none"
                  >
                    <circle cx={10} cy={10} r={10} fill="#6C18BA" />
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M14.238 5.762a5.963 5.963 0 0 0-4.86-1.732c-2.753.277-5.018 2.512-5.326 5.265a5.988 5.988 0 0 0 11.356 3.285.751.751 0 0 0-.675-1.08.73.73 0 0 0-.66.398 4.496 4.496 0 0 1-5.1 2.482 4.46 4.46 0 0 1-3.36-3.39A4.502 4.502 0 0 1 10 5.5c1.245 0 2.355.517 3.165 1.335l-1.132 1.132c-.473.473-.143 1.283.524 1.283h2.693c.412 0 .75-.338.75-.75V5.807c0-.667-.81-1.005-1.283-.532l-.48.487z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              >
                <ContextualHelpVehicleSearch />
              </ContextualHelp>
            )}
          </>
        )}
      </StyledFeatureStatus>

      {!isEditing && !isHistoryRoute && !isSimulating && (
        <FeatureOverflowMenu replaceRoute={replaceRoute} />
      )}
    </StatusContainer>
  )
}

FeatureStatus.propTypes = {
  setSendToTestClicked: PropTypes.func,
  replaceRoute: PropTypes.func,
  setStatus: PropTypes.func,
  moduleName: PropTypes.string,
  revision: PropTypes.string,
  status: PropTypes.string,
  featureIsValid: PropTypes.bool,
  isEditing: PropTypes.bool,
  isHistoryRoute: PropTypes.bool,
  setConfirmActivate: PropTypes.func,
  setConfirmObsolete: PropTypes.func,
  renderHarnessBtn: PropTypes.func,
}

export default FeatureStatus
